import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../components/hooks/useTableSortInit';
import useSelectedTenant from '../../../../../components/hooks/useSelectedTenant';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetBanksBIZcore, 
  UpdateBankBIZcore 
} from '../../../../../redux/actions/business/core/banksAction';

import BanksModal from './BanksModal';

import global from "../../../../../global.scss";

const BanksPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const tenantId = useSelectedTenant();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [banksModalOpen, setBanksModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [banksList, setBanksList] = useState([]);
  const [banksTotal, setBanksTotal] = useState(0);

  const [banksMenuOpen, setBanksMenuOpen] = useState(false);
  const [banksMenuAnchor, setBanksMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Bank",
      sort: "bank",
    },
    {
      label: "Bank Logo",
      sort: "bank_logo",
    },
    {
      label: "Key Identifier",
      sort: "key_identifier",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetBanksBIZcore(tenantId, page+1, rowsPerPage, query, setLoading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatBanksList(state.banksList.records || []);
      setBanksTotal(state.banksList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatBanksList = (list) => {
    setBanksList(list)
  };

  const handleSwitchBank = (id, bank, bool) => {
    let obj = {
      ...bank,
      is_active: bool,
    }
    dispatch(UpdateBankBIZcore(tenantId, id, obj, setLoading)).then(() => {
      dispatch(GetBanksBIZcore(tenantId, page+1, rowsPerPage, "", setLoading));
    })
  };

  return (
    <Box>

      <BanksModal 
        open={banksModalOpen}
        setOpen={setBanksModalOpen}
        title={modalTitle}
        update={update}
        page={page}
        rowsPerPage={rowsPerPage}
        />

      <TopBar
        title={"Banks"}
        search={search}
        searchPlaceholder={"Search id, bank or identifier"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Bank"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Bank");
          setBanksModalOpen(!banksModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          banksList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No bank found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                banksList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.bank || <NA />}</TableCell>
                    <TableCell>{row.bank_logo || <NA />}</TableCell>
                    <TableCell>{row.key_identifier || <NA />}</TableCell>
                    <TableCell>
                      <Switch 
                        checked={row.is_active}
                        onChange={(e) => {
                          handleSwitchBank(row.id, row, e.target.checked)
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setBanksMenuAnchor(e.currentTarget);
                          setBanksMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.bank || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={banksTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={banksMenuOpen}
        anchorEl={banksMenuAnchor}
        onClose={() => setBanksMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setBanksMenuOpen(false);
            setBanksModalOpen(!banksModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default BanksPage;